import { useRef, type KeyboardEvent, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface TabOption<T extends string> {
  value: T;
  label: string;
  /** Sits after the label as a muted count — "Collections 128". */
  badge?: ReactNode;
  icon?: ReactNode;
}

export interface TabsProps<T extends string> {
  options: readonly TabOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** Names the set for screen readers — "Fee views", not just "Tabs". */
  label: string;
  /**
   * How the strip is drawn.
   *
   * `underline` is the default and belongs at the top of a panel, where the
   * line under the selected tab is also the panel's own top edge. `segmented`
   * is the pill-shaped control for a short, closed set of alternatives — two or
   * three views of the same list — where the choice reads better as one switch
   * than as a row of headings.
   */
  variant?: 'underline' | 'segmented';
  className?: string;
}

/**
 * The product's tab strip, for a screen that shows two views of one subject.
 *
 * Built here rather than in the page that needed it first, because CLAUDE.md §3
 * says so: the second screen wanting tabs must add a prop, not a second copy of
 * this markup.
 *
 * A real tablist, not a row of buttons that look like one. `role="tablist"` with
 * roving focus means left and right arrows move between tabs and only the
 * selected one is in the tab order — which is what a screen reader user expects
 * the moment they hear "tab". Each panel is the caller's; it only needs
 * `role="tabpanel"` and an `id` matching `aria-controls`, which `tabPanelId`
 * below spells for both sides so the two cannot drift apart.
 */
export function Tabs<T extends string>({
  options,
  value,
  onChange,
  label,
  variant = 'underline',
  className,
}: TabsProps<T>) {
  const listRef = useRef<HTMLDivElement>(null);
  const isSegmented = variant === 'segmented';

  /** Left and right wrap around, as the tab pattern specifies. */
  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const delta = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (delta === 0) return;

    event.preventDefault();
    const current = options.findIndex((option) => option.value === value);
    const next = options[(current + delta + options.length) % options.length];
    if (!next) return;

    onChange(next.value);
    // Focus follows selection in an automatic tablist, so the newly selected
    // tab has to take the focus the old one is giving up.
    listRef.current
      ?.querySelector<HTMLButtonElement>(`[data-tab="${next.value}"]`)
      ?.focus();
  }

  return (
    <div
      ref={listRef}
      role="tablist"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={cn(
        'flex items-center overflow-x-auto no-scrollbar scrollbar-none',
        isSegmented
          ? 'gap-1 rounded-pill border border-line bg-surface-2 p-1'
          : 'gap-4 sm:gap-8 border-b border-slate-200 dark:border-slate-800 px-1',
        className,
      )}
    >
      {options.map((option) => {
        const isSelected = option.value === value;

        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            data-tab={option.value}
            id={`${option.value}-tab`}
            aria-selected={isSelected}
            aria-controls={tabPanelId(option.value)}
            tabIndex={isSelected ? 0 : -1}
            onClick={(e) => {
              onChange(option.value);
              e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }}
            className={cn(
              'flex shrink-0 items-center gap-1.5 whitespace-nowrap',
              'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
              isSegmented
                ? cn(
                    'flex-1 justify-center rounded-pill px-4 py-2 text-sm',
                    isSelected
                      ? 'bg-paper font-black text-indigo-600 dark:text-indigo-400 shadow-card'
                      : 'text-slate-500 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white font-bold',
                  )
                : cn(
                    'pb-2.5 sm:pb-3 text-[13px] sm:text-[14.5px] font-black transition-colors cursor-pointer relative',
                    isSelected
                      ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-600 dark:border-indigo-400 -mb-px'
                      : 'border-b-2 border-transparent text-slate-500 hover:text-slate-900 dark:text-slate-300 dark:hover:text-white font-bold',
                  ),
            )}
          >
            {option.icon}
            {option.label}
            {option.badge !== undefined && (
              <span
                className={cn(
                  'rounded-full px-1.5 sm:px-2 py-0.5 text-[11px] sm:text-xs font-black tabular-nums ml-0.5 sm:ml-1',
                  isSelected
                    ? 'bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300'
                    : 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400',
                )}
              >
                {option.badge}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/** The id a panel must carry so its tab's `aria-controls` finds it. */
export function tabPanelId(value: string): string {
  return `${value}-panel`;
}
